import React, { useCallback, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  useWindowDimensions,
} from 'react-native';
import { Feather, Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaSpacing } from '../../styles/theme';
import type { MenuAnchor } from './OverflowMenu';

const WIDE_BREAKPOINT = 768;

interface RecipeActionsBarProps {
  onCookMode?: () => void;
  onAddToPlan: () => void;
  onShare: () => void;
  onMore: (anchor: MenuAnchor) => void;
  /** True when the recipe already sits in an upcoming meal slot. */
  isPlanned?: boolean;
  /** That slot, e.g. "Mercredi soir" — the label of the reschedule button. */
  slotLabel?: string;
  /** Opens the plan sheet against the existing meal, to move or remove it. */
  onReschedule?: () => void;
}

/**
 * Desktop actions bar: share, overflow, then two contextual buttons.
 *
 * The primary action follows the plan status — a recipe you have not planned
 * wants "Planifier"; one that is already on the week wants "Cuisiner". The
 * secondary slot then shows *when* it is planned, and tapping it opens the same
 * plan sheet in move mode. Editing, deleting, rating a cook and "voir au plan"
 * live in the ⋯ menu.
 *
 * Renders nothing on narrow layouts: mobile carries the same pair of actions in
 * the sticky bottom bar instead, with share and ⋯ on the hero.
 */
export const RecipeActionsBar: React.FC<RecipeActionsBarProps> = ({
  onCookMode,
  onAddToPlan,
  onShare,
  onMore,
  isPlanned,
  slotLabel,
  onReschedule,
}) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const moreRef = useRef<View>(null);

  const handleMore = useCallback(() => {
    const node = moreRef.current;
    if (node && typeof node.measureInWindow === 'function') {
      node.measureInWindow((x, y, w, h) => {
        onMore({ top: y + h + 6, right: Math.max(width - (x + w), 8) });
      });
    } else {
      onMore({ top: 120, right: 56 });
    }
  }, [onMore, width]);

  if (width < WIDE_BREAKPOINT) return null;

  const cookLabel = t('recipeDetail.cookMode', 'Mode cuisson');
  const planLabel = t('recipeDetail.plan', 'Planifier');
  const rescheduleLabel = slotLabel ?? t('recipeDetail.moveMealTitle', 'Déplacer ce repas');

  const CookOutline = onCookMode ? (
    <TouchableOpacity style={styles.outlineButton} onPress={onCookMode} activeOpacity={0.85}>
      <Ionicons name="flame" size={17} color={Paprika.textPrimary} />
      <Text style={styles.outlineButtonText}>{cookLabel}</Text>
    </TouchableOpacity>
  ) : null;

  const CookAccent = onCookMode ? (
    <TouchableOpacity style={styles.accentButton} onPress={onCookMode} activeOpacity={0.85}>
      <Ionicons name="flame" size={17} color="#fff" />
      <Text style={styles.accentButtonText}>{cookLabel}</Text>
    </TouchableOpacity>
  ) : null;

  const PlanAccent = (
    <TouchableOpacity style={styles.accentButton} onPress={onAddToPlan} activeOpacity={0.85}>
      <Ionicons name="calendar" size={17} color="#fff" />
      <Text style={styles.accentButtonText}>{planLabel}</Text>
      <Feather name="chevron-right" size={15} color="#fff" style={styles.chevron} />
    </TouchableOpacity>
  );

  const RescheduleOutline = onReschedule ? (
    <TouchableOpacity
      style={styles.outlineButton}
      onPress={onReschedule}
      activeOpacity={0.85}
      accessibilityRole="button"
      accessibilityLabel={t('recipeDetail.moveMealTitle', 'Déplacer ce repas')}
    >
      <Ionicons name="calendar" size={17} color={Paprika.textPrimary} />
      <Text style={styles.outlineButtonText}>{rescheduleLabel}</Text>
      <Feather name="chevron-right" size={15} color={Paprika.textSecondary} style={styles.chevron} />
    </TouchableOpacity>
  ) : null;

  return (
    <View style={styles.row}>
      <TouchableOpacity
        style={styles.iconButton}
        onPress={onShare}
        activeOpacity={0.75}
        accessibilityRole="button"
        accessibilityLabel={t('recipeDetail.share', 'Partager')}
      >
        <Ionicons name="share-outline" size={18} color={Paprika.textSecondary} />
      </TouchableOpacity>

      <View ref={moreRef} collapsable={false}>
        <TouchableOpacity
          style={styles.iconButton}
          onPress={handleMore}
          testID="recipe-more"
          activeOpacity={0.75}
          accessibilityRole="button"
          accessibilityLabel={t('recipeDetail.more', 'Plus')}
        >
          <Ionicons name="ellipsis-horizontal" size={19} color={Paprika.textSecondary} />
        </TouchableOpacity>
      </View>

      {isPlanned ? (
        <>
          {RescheduleOutline}
          {CookAccent}
        </>
      ) : (
        <>
          {CookOutline}
          {PlanAccent}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  iconButton: {
    width: 44,
    height: 44,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: Paprika.border,
    backgroundColor: Paprika.surfaceLowest,
  },
  outlineButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: PaprikaSpacing.sm,
    height: 44,
    paddingHorizontal: PaprikaSpacing.md,
    borderRadius: 13,
    borderWidth: 1,
    borderColor: Paprika.border,
    backgroundColor: Paprika.surfaceLowest,
  },
  outlineButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    color: Paprika.textPrimary,
  },
  accentButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: PaprikaSpacing.sm,
    height: 44,
    paddingHorizontal: PaprikaSpacing.md,
    borderRadius: 13,
    backgroundColor: Paprika.accent,
  },
  accentButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    color: '#fff',
  },
  chevron: {
    opacity: 0.55,
  },
});

export default RecipeActionsBar;
